import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { InvoiceEntity } from './invoice.entity';
import { ItemEntity } from './item.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { ShipmentDetailEntity } from './shipment-detail.entity';

@Index('invoice_detail__fk_invoice__id_fk', ['fkInvoiceId'], {})
// PD-17025 — Sales Comparison by Customer and Sales by Customer and Product
// Group add up every sales line of the invoices in a date window. Through the
// plain invoice index each line was then read from the table: on Macomb a
// full year is 1.1M lines, about 3.8 s per window, of which reading the rows
// was about 2.7 s (the comparison reads two windows). This index holds
// everything those scans read, in the order they use it: the invoice
// (join), then the two line filters (not excluded from sales, not deleted)
// so they narrow the lookup, then the item, quantity, sales, gross profit and
// both costs, so no table row is read. MySQL rates this wider index as dearer
// than the plain one and never picks it on its own, so those reports (and Top
// Clients by Revenue, PD-17141) name it in an optimizer hint
// (INVOICE_DETAIL_SALES_LINES_INDEX in crm-api's invoice-sales-sql.utils).
// Renaming it means changing that name too.
@Index(
	'invoice_detail__fk_invoice__id_sales_lines',
	[
		'fkInvoiceId',
		'excludeFromSales',
		'deletedDate',
		'fkItemId',
		'quantity',
		'subtotal',
		'totalGp',
		'sellCost',
		'acquisitionCost',
	],
	{},
)
@Index('invoice_detail__fk_item__id_fk', ['fkItemId'], {})
@Index(
	'invoice_detail__fk_order_detail__id__deleted_userId',
	['deletedUserId', 'fkOrderDetailId'],
	{},
)
@Index('invoice_detail__fk_order_detail__id_fk', ['fkOrderDetailId'], {})
@Index('invoice_detail_pk_2', ['externalId'], { unique: true })
@Index(
	'invoice_detail_itemExternalId_deletedDate',
	['itemExternalId', 'deletedDate'],
	{},
)
@Entity('invoice_detail')
export class InvoiceDetailEntity extends BaseEntity {
	@Column('varchar', {
		name: 'externalId',
		nullable: true,
		unique: true,
		length: 64,
	})
	public externalId!: string | null;

	@Column('varchar', { name: 'itemExternalId', nullable: true, length: 64 })
	public itemExternalId!: string | null;

	@Column('int', { name: '_fk_invoice__id', unsigned: true })
	public fkInvoiceId!: number;

	@Column('int', { name: '_fk_item__id', nullable: true, unsigned: true })
	public fkItemId!: number | null;

	@Column('int', {
		name: '_fk_order_detail__id',
		nullable: true,
		unsigned: true,
	})
	public fkOrderDetailId!: number | null;

	@Column('int', { name: 'lineNumber', nullable: true })
	public lineNumber!: number | null;

	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	/**
	 * Lot number(s) allocated to this line, as printed on the invoice — e.g. `AE06421`, or
	 * `AE06420 (1), AE06419 (1)` when a line was filled from several lots. Kept as text rather
	 * than a FK to `item_lot` because ERPs (Blue Link) allocate one line from several lots.
	 * Populated by the integration import; null for tenants whose ERP does not provide it.
	 */
	@Column('varchar', { name: 'lotNumber', nullable: true, length: 255 })
	public lotNumber!: string | null;

	/**
	 * PD-15754 — true for a line that does not count toward sales or GP, such
	 * as a restocking fee or a finance charge. The line still counts toward the
	 * invoice total and balance. The invoice header caches the flagged lines'
	 * totals in `nonSalesSubtotal` / `nonSalesGP` for the header-level reports.
	 */
	@Column('tinyint', {
		name: 'excludeFromSales',
		width: 1,
		default: () => "'0'",
	})
	public excludeFromSales!: boolean;

	@Column('int', { name: 'quantity', nullable: true })
	public quantity!: number | null;

	@Column('decimal', { name: 'price', nullable: true, precision: 15, scale: 6 })
	public price!: string | null;

	@Column('decimal', {
		name: 'minimumPrice',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public minimumPrice!: string | null;

	@Column('decimal', {
		name: 'subtotal',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public subtotal!: string | null;

	@Column('decimal', {
		name: 'freight',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public freight!: string | null;

	@Column('decimal', { name: 'tax', nullable: true, precision: 13, scale: 2 })
	public tax!: string | null;

	@Column('decimal', {
		name: 'discount',
		nullable: true,
		precision: 21,
		scale: 10,
	})
	public discount!: string | null;

	@Column('varchar', { name: 'discountType', length: 32, default: () => "'$'" })
	public discountType!: string;

	@Column('decimal', { name: 'total', nullable: true, precision: 13, scale: 2 })
	public total!: string | null;

	@Column('decimal', {
		name: 'acquisitionCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public acquisitionCost!: string | null;

	@Column('decimal', {
		name: 'sellCost',
		nullable: true,
		precision: 15,
		scale: 6,
	})
	public sellCost!: string | null;

	@Column('decimal', {
		name: 'totalGP',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGp!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('decimal', {
		name: 'conversionFactor',
		precision: 11,
		scale: 4,
		default: () => "'1.0000'",
	})
	public conversionFactor!: string;

	@ManyToOne(() => InvoiceEntity, (invoice) => invoice.invoiceDetails, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public invoice!: InvoiceEntity;

	@ManyToOne(() => ItemEntity, (item) => item.invoiceDetails, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(
		() => OrderDetailEntity,
		(orderDetail) => orderDetail.invoiceDetails,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: '_fk_order_detail__id', referencedColumnName: 'id' }])
	public orderDetail!: OrderDetailEntity;

	@OneToMany(
		() => ShipmentDetailEntity,
		(shipmentDetail) => shipmentDetail.invoiceDetail,
		{},
	)
	public shipmentDetails!: Array<ShipmentDetailEntity>;
}
